'use client'

import useActiveItemOffer from '@/utils/hooks/useActiveItemOffer'
import CancelOfferModal from '@/components/offers/cancel-offer-modal'
import type { Offer as CancellableOffer } from '@/utils/offers'
import React, { FC, ReactElement } from 'react'
import dynamic from 'next/dynamic'
import { offersEnabledFor } from '@/utils/offers'
import useIsHydrated from '@/utils/hooks/useIsHydrated'

const ItemOfferModal = dynamic(() => import('@/components/offers/item-offer-modal'))
import InscriptionActionTransfer from '@/components/inscription-actions/inscription-action-transfer'
import InscriptionActionSell from './inscription-action-sell'
import InscriptionActionCancel from './inscription-action-cancel'
import InscriptionActionBuy from './inscription-action-buy'
import InscriptionActionView from './inscription-action-view'
import { Inscription } from '@/utils/types/inscription'
import Wallet from '@/utils/wallet'
import event from '@/utils/event'
import classNames from 'classnames'

import TransferIcon from '@/icons/transfer'
import MarketIcon from '@/icons/market'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'

const InscriptionActions: FC<{
	isDetail?: boolean
	inscription: Inscription
	owner: boolean
	className?: string
	actionClassName?: string
	actionButtonColor: 'gray' | 'black'
	hideView?: boolean
	onLocalUpdate?: () => void
	onListClick?: () => void
	onTransferClick?: () => void
	rune?: boolean
	isBRC20?: boolean
	onClickBuy?: () => void
	onlyShowPrice?: boolean
	selected?: boolean
	collectionCard?: { slug: string; name: string }
}> = (props): ReactElement => {
	const { onClickBuy, inscription, className, onLocalUpdate, isDetail, onlyShowPrice, selected } =
		props
	const hydrated = useIsHydrated()
	const [offerOpen, setOfferOpen] = React.useState(false)
	const activeItemOffer = useActiveItemOffer(
		inscription.id,
		!!props.collectionCard && hydrated && offersEnabledFor(props.collectionCard.slug, inscription)
	)
	const [cancellingOffer, setCancellingOffer] = React.useState<CancellableOffer | null>(null)
	const openOfferAction = () =>
		activeItemOffer ? setCancellingOffer(activeItemOffer) : setOfferOpen(true)
	const [openSell, setOpenSell] = React.useState(false)
	const [openTransfer, setOpenTransfer] = React.useState(false)
	const openInscriptionModal = useInscriptionModal()

	const [localPrice, setLocalPrice] = React.useState(
		inscription?.escrow ? inscription?.escrow.satoshi_price / 1e8 : null
	)

	React.useLayoutEffect(() => {
		requestAnimationFrame(() => {
			setLocalPrice(inscription?.escrow ? inscription?.escrow.satoshi_price / 1e8 : null)
		})
	}, [inscription])

	const owner =
		props.owner ||
		inscription?.escrow?.seller_address === Wallet.getTaprootAddress() ||
		(Wallet.provider.capabilities.includes('payments-wallet') &&
			inscription?.escrow?.seller_address === Wallet.balanceAddress())

	const forSale = React.useMemo(
		() => !!localPrice && !inscription?.escrow?.bought_at,
		[localPrice, inscription]
	)

	React.useEffect(() => {
		const handleListing = (params: any) => {
			if (params.type === 'success' && params.id === inscription?.id && params.price) {
				setLocalPrice(parseFloat(params.price))
				onLocalUpdate && onLocalUpdate()
			}
		}
		event.listen('list-item', handleListing)

		return () => {
			event.removeListener('list-item', handleListing)
		}
	}, [inscription?.id, onLocalUpdate])

	const onSetOpenTransfer = React.useCallback((open: any) => {
		if (open) {
			setOpenSell(false)
		}
		setOpenTransfer(open)
	}, [])

	const onSetOpenSell = React.useCallback((open: any) => {
		if (open) {
			setOpenTransfer(false)
		}
		setOpenSell(open)
	}, [])

	const handleLocalUpdate = React.useCallback(
		(listed_price?: any) => {
			setLocalPrice(listed_price || null)
			onLocalUpdate && onLocalUpdate()
		},
		[onLocalUpdate]
	)

	const handleLocalUpdateCancel = React.useCallback(
		(listed_price?: any) => {
			setLocalPrice(listed_price || null)
			setOpenSell(false)
			event.emit('list-item', {
				type: 'cancel',
				cancelled: true,
				id: inscription.id
			})
		},
		[inscription.id]
	)

	const handleClickthrough = React.useCallback((e: any) => {
		if (e?.target?.href?.includes('inscription')) {
			e.stopPropagation()
		} else {
			e.preventDefault()
			e.stopPropagation()
		}
	}, [])

	return (
		<>
			{(offerOpen || cancellingOffer) && (
				<div
					onClick={(event) => event.stopPropagation()}
					onKeyDown={(event) => event.stopPropagation()}
				>
					{cancellingOffer ? (
						<CancelOfferModal offer={cancellingOffer} onClose={() => setCancellingOffer(null)} />
					) : (
						<ItemOfferModal
							inscription={{
								...inscription,
								collection: props.collectionCard || inscription.collection
							}}
							listedPriceSats={forSale ? Math.round(Number(localPrice) * 1e8) : null}
							onClose={() => setOfferOpen(false)}
						/>
					)}
				</div>
			)}
			<div
				onClick={handleClickthrough}
				className={classNames({ [props.className as any]: !!className }, 'flex flex-col w-full')}
				role="button"
				tabIndex={0}
				onKeyDown={(event) => {
					if (event.target !== event.currentTarget) {
						event.stopPropagation()
						return
					}
					onKeyboardClick(handleClickthrough)?.(event)
				}}
			>
				{!owner && forSale && inscription?.escrow && (
					<InscriptionActionBuy
						onClickBuy={onClickBuy}
						inscription={inscription}
						isBRC20={props?.isBRC20}
						onlyShowPrice={props.onlyShowPrice}
						selected={selected}
						stackedActions={!!props.collectionCard}
						offerLabel={activeItemOffer ? 'Cancel offer' : 'Make offer'}
						onClickOffer={
							props.collectionCard && hydrated && offersEnabledFor(props.collectionCard.slug, inscription)
								? openOfferAction
								: undefined
						}
					/>
				)}
				{!props.hideView && !owner && !forSale && <InscriptionActionView id={inscription?.id} />}
				{owner && forSale && (
					<InscriptionActionCancel
						onComplete={handleLocalUpdateCancel}
						price={localPrice as any}
						id={inscription?.id}
						rune={props.rune}
						onlyShowPrice={props.onlyShowPrice}
					/>
				)}
				{!onlyShowPrice && isDetail && owner && !forSale && (
					<div className="flex flex-col gap-3">
						{Wallet.type() !== 'hiro' && (
							<InscriptionActionTransfer
								inscription={inscription}
								open={openTransfer}
								setOpen={onSetOpenTransfer}
								className={props.actionClassName}
								onSuccess={handleLocalUpdate}
								buttonColor={props?.actionButtonColor}
							/>
						)}
						<InscriptionActionSell
							inscription={inscription}
							open={openSell}
							setOpen={onSetOpenSell}
							className={props.actionClassName}
							buttonColor={props?.actionButtonColor}
						/>
					</div>
				)}
				{!isDetail && owner && !forSale && (
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<div
								onClick={props.onListClick}
								className="size-7 bg-[#1D2939] rounded-lg flex items-center justify-center"
								role="button"
								tabIndex={0}
								onKeyDown={onKeyboardClick(props.onListClick)}
							>
								<MarketIcon className="size-4 fill-foreground" />
							</div>
							<div
								onClick={props.onTransferClick}
								className="size-7 bg-[#1D2939] rounded-lg flex items-center justify-center"
								role="button"
								tabIndex={0}
								onKeyDown={onKeyboardClick(props.onTransferClick)}
							>
								<TransferIcon className="size-4 fill-foreground" />
							</div>
						</div>
						<button
							type="button"
							onClick={(evt) => {
								evt.preventDefault()
								evt.stopPropagation()
								openInscriptionModal({ id: inscription?.id, inscription })
							}}
							className="h-7 px-4 text-sm font-semibold rounded-full items-center flex justify-center cursor-pointer [background:var(--gradient-epic)]"
						>
							Details
						</button>
					</div>
				)}
			</div>
		</>
	)
}

export default InscriptionActions
